import type { SeriesClickCallbackFunction } from 'highcharts';
import { useMemo } from 'react';

interface BarChartOptions {
  subtitle?: string;
  orientation?: 'horizontal' | 'vertical';
  onClick?: SeriesClickCallbackFunction;
  stackedBars?: boolean;
  enableLegend?: boolean;
  /**
   * Formats the aggregated point value for tooltips and data labels (e.g. the
   * 2-dp consequence cost formatter). When omitted, the raw `{point.y}` string
   * formats are retained unchanged (RSP-5735).
   */
  valueFormatter?: (value: number) => string;
}

/**
 * Builds the bar tooltip point string for a formatted value. Exported so the
 * `this`-bound Highcharts callbacks can stay thin and be unit-tested without
 * faking a full `Highcharts.Point` context.
 */
export const formatBarTooltipPoint = (
  seriesName: string | undefined,
  value: number,
  valueFormatter: (value: number) => string
): string => `${seriesName ?? ''}: ${valueFormatter(value)}`;

export const useGetBarChartDefaultOptions = ({
  subtitle,
  orientation,
  onClick,
  stackedBars,
  enableLegend,
  valueFormatter,
}: BarChartOptions): Highcharts.Options => {
  return useMemo(() => {
    const options: Highcharts.Options = {
      chart: {
        type: (orientation ?? 'horizontal') === 'horizontal' ? 'bar' : 'column',
      },
      subtitle: {
        text: subtitle || '',
      },
      yAxis: {
        min: 0,
        allowDecimals: false,
        endOnTick: false,
      },
      legend: {
        enabled: enableLegend ?? false,
      },
      plotOptions: {
        series: {
          stacking: stackedBars ? 'normal' : undefined,
          events: {
            click: onClick,
          },
          ...(valueFormatter
            ? {
                dataLabels: {
                  formatter: function barDataLabelFormatter(
                    this: Highcharts.Point
                  ) {
                    return valueFormatter(this.y ?? 0);
                  },
                },
              }
            : {}),
        },
      },
      tooltip: {
        headerFormat: '<b>{category}</b><br/>',
        ...(valueFormatter
          ? {
              pointFormatter: function barTooltipPointFormatter(
                this: Highcharts.Point
              ) {
                return formatBarTooltipPoint(
                  this.series.name,
                  this.y ?? 0,
                  valueFormatter
                );
              },
            }
          : { pointFormat: '{series.name}: {point.y}' }),
      },
    };

    return options;
  }, [
    orientation,
    subtitle,
    enableLegend,
    stackedBars,
    onClick,
    valueFormatter,
  ]);
};
